// =============================================================================
// RepeatCheckNoteModal.jsx — the required "Repeat check" note at Confirm & Send
// =============================================================================
// Possible-repeat flag, Phase A (db-210; David, 21 Oct 2026: the note is REQUIRED).
// Shown by ApprovalDecisions.confirmAndSend when the request has a flagged fee or
// device, AFTER the existing backstops and BEFORE anything is saved. Cancel = nothing
// saved (ephemeral-staging rule). The text is saved by the caller as a staff-only
// internal note (kind "repeat_check") only after the confirm itself succeeds.
//
// Props:
//   items     - FlaggedConfirmItem[] from src/services/repeatCheck.ts
//   onConfirm - (noteText: string) => void   (trimmed, never empty)
//   onCancel  - () => void
// =============================================================================
import React from "react";
import { formatRef, money } from "../../utils/helpers";
import { ModalBackdrop } from "../form/FormUI.jsx";

const BROWN = "#8A4B00";
const BROWN_DARK = "#5D3A00";

export function RepeatCheckNoteModal({ items, onConfirm, onCancel }) {
  const [note, setNote] = React.useState("");
  const trimmed = note.trim();
  const list = items || [];
  return (
    <ModalBackdrop
      onClose={onCancel}
      draggable
      dragTitle="Repeat check before you send"
      // ModalBackdrop gives the dialog no background of its own — set it here, or the
      // page shows through the pop-up (found in the 24 Oct staging test).
      contentStyle={{
        width: 640,
        maxWidth: "95vw",
        background: "#fff",
        borderRadius: 10,
        boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      }}
    >
      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 14, color: "#1A2E2B" }}>
          This request has {list.length === 1 ? "an item" : `${list.length} items`} flagged as a{" "}
          <strong>possible repeat</strong>. Say how you checked it. This note is for Zephyr staff
          only — the DFV Service never sees it.
        </div>
        <div
          style={{
            background: "#FFF8E1",
            border: "1px solid #FFB300",
            borderRadius: 6,
            padding: "8px 12px",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: BROWN }}>Flagged items</div>
          {list.map(function (it) {
            return (
              <div key={it.key} style={{ fontSize: 13, color: BROWN_DARK }}>
                <div>
                  {`Child ${it.childIndex + 1} · ${it.label}${it.desc ? ` · ${it.desc}` : ""} · ${money(it.amount)} · your decision: `}
                  <strong>{it.decision}</strong>
                </div>
                <div style={{ fontSize: 12 }}>
                  {`Earlier: ${it.earlier
                    .map(function (e) {
                      return `${formatRef(e.refNo)} (${e.decision})`;
                    })
                    .join(" · ")}${it.strong ? " — strong match" : ""}`}
                </div>
              </div>
            );
          })}
        </div>
        <label
          htmlFor="repeat-check-note"
          style={{ fontSize: 13, fontWeight: 700, color: "#1A2E2B" }}
        >
          Repeat check note (required)
        </label>
        <textarea
          id="repeat-check-note"
          rows={4}
          value={note}
          onChange={function (e) {
            setNote(e.target.value);
          }}
          placeholder="e.g. Approved as the final month — told the service support ends at Term 4."
          style={{
            fontFamily: "inherit",
            fontSize: 14,
            padding: "8px 10px",
            border: "2px solid #00897B",
            borderRadius: 6,
            resize: "vertical",
            color: "#1A2E2B",
          }}
        />
        <div style={{ fontSize: 12, color: "#607D79" }}>
          Saved as an internal note on the request when you press Confirm &amp; Send. Nothing is
          saved if you cancel.
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: "10px 20px",
              fontSize: 14,
              fontWeight: 600,
              fontFamily: "inherit",
              border: "1px solid #CFE0DE",
              borderRadius: 8,
              background: "#fff",
              color: "#1A2E2B",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!trimmed}
            onClick={function () {
              if (trimmed) onConfirm(trimmed);
            }}
            style={{
              padding: "10px 24px",
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "inherit",
              border: "none",
              borderRadius: 8,
              background: trimmed ? "#00897B" : "#B0BEC5",
              color: "#fff",
              cursor: trimmed ? "pointer" : "not-allowed",
            }}
          >
            Confirm &amp; Send
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
